import { z } from 'zod';
import type { ConnectionSummary } from './connections';
import { serviceIdSchema } from './plugin-requirements';

/**
 * The `connections` capability (`docs/adr-connections-hub.md`): the user's connections of the
 * apps the plugin names in `requires.connections` or `contributions.connectors`, kept by the
 * connection hub. `list` and `default` return summaries; `lease` returns the values to use now,
 * which the backend keeps in memory only and leases again once they expire; `fetch` has the hub
 * send a request to the type's API with the connection, so the backend never sees it.
 */
export const CONNECTIONS_CAPABILITY = 'connections';

/** A request the hub sends to a connection's API (`fetch`). */
export const pluginConnectionFetchSchema = z
  .object({
    method: z.enum(['GET', 'HEAD', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS']).default('GET'),
    /** Absolute, or relative to the API the type declares. */
    url: z.string().min(1).max(4096),
    headers: z
      .record(z.string().regex(/^[A-Za-z0-9!#$%&'*+.^_`|~-]{1,64}$/), z.string().max(8192))
      .refine((headers) => Object.keys(headers).length <= 32, 'At most 32 headers.')
      .optional(),
    body: z
      .string()
      .max(22 * 1024 * 1024)
      .optional(),
    /** `base64` for a binary body (a multipart form with files). */
    bodyEncoding: z.enum(['utf8', 'base64']).default('utf8'),
    /** `base64` to get a binary answer as is. */
    responseEncoding: z.enum(['utf8', 'base64']).default('utf8'),
    /** For slow APIs (image generation): at most 5 minutes. */
    timeoutMs: z.number().int().min(1000).max(300_000).optional(),
  })
  .strict();

export const pluginConnectionsRequestSchema = z.discriminatedUnion('method', [
  z.object({
    method: z.literal('list'),
    input: z.object({ type: serviceIdSchema.optional() }).strict().default({}),
  }),
  z.object({ method: z.literal('default'), input: z.object({ type: serviceIdSchema }).strict() }),
  z.object({ method: z.literal('lease'), input: z.object({ id: z.uuid() }).strict() }),
  z.object({
    method: z.literal('fetch'),
    input: pluginConnectionFetchSchema.extend({ id: z.uuid() }).strict(),
  }),
]);
export type PluginConnectionFetch = z.input<typeof pluginConnectionFetchSchema>;

/** A proxied request's answer: up to 16 MB (text, or base64 when asked), and the headers worth showing. */
export type PluginConnectionResponse = {
  status: number;
  headers: Record<string, string>;
  body: string;
  encoding?: 'utf8' | 'base64';
};
export type PluginConnectionsRequest = z.infer<typeof pluginConnectionsRequestSchema>;

/** A connection as a plugin sees it: never its secret values. */
export type PluginConnection = Pick<
  ConnectionSummary,
  | 'id'
  | 'type'
  | 'name'
  | 'owner'
  | 'authId'
  | 'authKind'
  | 'config'
  | 'scopes'
  | 'status'
  | 'declaration'
  | 'isDefault'
  | 'generation'
>;

/** What a plugin sees of a connection. */
export function pluginConnection(connection: ConnectionSummary): PluginConnection {
  return {
    id: connection.id,
    type: connection.type,
    name: connection.name,
    owner: connection.owner,
    authId: connection.authId,
    authKind: connection.authKind,
    config: connection.config,
    scopes: connection.scopes,
    status: connection.status,
    declaration: connection.declaration,
    isDefault: connection.isDefault,
    generation: connection.generation,
  };
}

export type PluginConnectionLease = {
  connection: PluginConnection;
  /**
   * The values to use: every entered value, secrets included; for an OAuth grant, its access
   * token (`access_token`, `token_type`), never its refresh token.
   */
  credential: Record<string, string>;
  /** When to lease again; the values may have changed by then. */
  expiresAt: string;
  generation: number;
};
